import React, {useContext} from "react";
import {useHistory, useLocation} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {guestViewsUrlMap} from "configuration/constants";
import {CrmContext} from "routes/CrmPage";
import {useTheme} from "connectui/theme/ThemeProvider";
import {buildStyles} from "connectui/component/buildStyles";
import ICONS from "connectui/icons/iconMap";
import FlexContainer from "connectui/component/layout/FlexContainer";
import Text from "connectui/component/display/Text";
import SvgIcon from "connectui/component/display/SvgIcon";
import ListComponent from "connectui/component/layout/list/ListComponent";
import ScrollableContainer from "connectui/component/layout/ScrollableContainer";
import ListItemContainer from "connectui/component/layout/list/ListItemContainer";
import Container from "connectui/component/layout/Container";
import ButtonIcon from "../../../../connectui/component/input/button/ButtonIcon";

const useStyles = (theme) =>
  buildStyles({
    container: {
      width: "100%",
      height: "100%",
      overflow: "hidden",
    },
    header: {
      width: "100%",
      marginTop: theme.spacing(4),
      padding: theme.spacing(0, 4),
    },
    icon: {
      width: "20px",
      height: "20px",
      color: theme.colors.textPrimary,
      marginRight: theme.spacing(2),
    },
    itemRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(3),
      width: "100%",
    },
    description: {
      color: theme.colors.textSecondary,
      fontSize: "12px",
      marginTop: theme.spacing(1),
    },
  });

const CrmViewItem = ({view, onClick, isSelected}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);

  const iconComponent = () => {
    const iconName = view.iconName || "eye";

    return (
      <SvgIcon
        component={ICONS[iconName]}
        className={classes.icon}
        sx={{
          color: isSelected ? theme.colors.primary : theme.colors.textSecondary,
          backgroundColor: isSelected ? theme.colors.primary50 : "transparent",
          padding: "4px",
          borderRadius: "4px",
        }}
      />
    );
  };

  return (
    <ListItemContainer
      sx={{
        backgroundColor: isSelected ? theme.colors.primary50 : "transparent",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2, 3),
        "&:hover": {
          backgroundColor: isSelected
            ? theme.colors.primary50
            : theme.colors.gray100,
        },
      }}
      onClick={onClick}
    >
      <Container className={classes.itemRow}>
        {iconComponent()}
        <FlexContainer flexDirection="column">
          <Text
            variant="textSM"
            weight="medium"
            color={isSelected ? "primary" : "gray700"}
          >
            {view.title}
          </Text>
          {view.description && (
            <Text
              variant="textXS"
              className={classes.description}
              sx={{
                color: isSelected
                  ? theme.colors.primary
                  : theme.colors.textSecondary,
              }}
            >
              {view.description}
            </Text>
          )}
        </FlexContainer>
      </Container>
    </ListItemContainer>
  );
};

const CrmViewsPanel = ({onClose}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const {setAppliedFilters, setFiltersOpen, setSearchText} =
    useContext(CrmContext);

  const searchParams = new URLSearchParams(location.search);
  const filtersParam = searchParams.get("filters");
  const columnsParam = searchParams.get("columns");

  const handleViewClick = (view, viewKey) => {
    setFiltersOpen(false);
    setSearchText("");

    if (viewKey === "all_guests" && !filtersParam && !columnsParam) {
      return;
    }

    const redirectUrl = view.redirect_url;
    if (redirectUrl) {
      const urlParams = new URLSearchParams(redirectUrl);
      const filtersValue = urlParams.get("filters");

      if (filtersValue) {
        try {
          const parsedFilters = JSON.parse(decodeURIComponent(filtersValue));
          setAppliedFilters(parsedFilters);
        } catch (e) {
          console.error("Failed to parse filters from URL:", e);
        }
      } else {
        setAppliedFilters([]);
      }

      const baseUrl = window.location.pathname;
      history.push(`${baseUrl}${redirectUrl}`);
    }
  };

  const parseUrlParams = (url) => {
    const params = new URLSearchParams(
      url.includes("?") ? url.split("?")[1] : url,
    );
    return {
      filters: params.get("filters")
        ? JSON.parse(decodeURIComponent(params.get("filters")))
        : [],
      columns: params.get("columns")
        ? JSON.parse(decodeURIComponent(params.get("columns")))
        : null,
    };
  };

  const getCurrentView = () => {
    try {
      // Parse current URL parameters
      const currentParams = {
        filters: filtersParam
          ? JSON.parse(decodeURIComponent(filtersParam))
          : [],
        columns: columnsParam
          ? JSON.parse(decodeURIComponent(columnsParam))
          : null,
      };

      // Special case for "all guests" view
      if (!filtersParam && !columnsParam) {
        return "all_guests";
      }

      // Compare current parameters with each view's parameters
      for (const [key, view] of Object.entries(guestViewsUrlMap)) {
        const viewParams = parseUrlParams(view.redirect_url);

        const filtersMatch =
          JSON.stringify(currentParams.filters) ===
          JSON.stringify(viewParams.filters);
        const columnsMatch =
          JSON.stringify(currentParams.columns) ===
          JSON.stringify(viewParams.columns);

        if (filtersMatch && columnsMatch) {
          return key;
        }
      }

      return null;
    } catch (error) {
      console.error("Error comparing views:", error);
      return null;
    }
  };

  const currentView = getCurrentView();

  return (
    <FlexContainer
      className={classes.container}
      flexDirection="column"
      gap={theme.spacing(6)}
    >
      <FlexContainer
        className={classes.header}
        gap={theme.spacing(3)}
        alignItems="center"
      >
        <FlexContainer
          justifyContent="space-between"
          alignItems="center"
          sx={{width: "100%"}}
        >
          <Text variant="displayXS" weight="semibold">
            {t("Views")}
          </Text>
          <ButtonIcon
            icon={
              <SvgIcon
                component={ICONS["layout-sidebar-left"]}
                sx={{
                  width: "28px",
                  height: "28px",
                  padding: "5px",
                  color: theme.colors.textSecondary,
                }}
              />
            }
            sx={{
              borderRadius: theme.spacing(2),
              cursor: "pointer",
              padding: 0,
              border: "none",
              backgroundColor: "transparent",
              marginTop: "3px",
              "&:hover": {backgroundColor: theme.colors.hoverGray},
            }}
            onClick={() => {
              onClose();
            }}
          />
        </FlexContainer>
      </FlexContainer>

      <ScrollableContainer
        width={"100%"}
        height={"100%"}
        p={theme.spacing(0, 4, 4)}
      >
        <ListComponent gap={theme.spacing(1)}>
          {Object.entries(guestViewsUrlMap).map(([key, view]) => (
            <CrmViewItem
              key={key}
              view={view}
              isSelected={currentView === key}
              onClick={() => handleViewClick(view, key)}
            />
          ))}
        </ListComponent>
      </ScrollableContainer>
    </FlexContainer>
  );
};

export default CrmViewsPanel;
